import React, { useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  ClearingHouse,
  EntityState,
  EntityStatus,
  EntityType,
  EntityTypeEnum,
  ReferenceDataEntityType,
  TradingEntityClearing,
  TradingEntityClearingType,
  WorkflowActions
} from 'api'
import { ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { SelectboxField } from 'view/components/options'
import { HorizontalLayout } from 'view/styles/constants'
import { OnOffProductsPanelGrid, OnOffProductsPanelGridRow } from 'view/shared/datagrid/products/OnOffProductsPanelGrid'
import { clearableProductSectorRecordSelector, productRecordSelector } from 'selectors/productSelectors'
import {
  createWorkflowAvailableSponsoredTradingEntityClearingSponsorIdsSelector,
  createWorkflowAvailableTradingEntityClearingClearingHouseIdsSelector
} from 'selectors/workflowSelectors'
import { SponsorHelper } from 'model'
import { Locale, Translation } from 'locale'
import { Content, GridWrapper } from './TradingEntityClearingDialogStyled'
import { getProductsRowData } from './tradingEntityClearingDialogHelpers'
import { createReferenceDataEntityHook, useAllEntities, useOnMount } from 'view/hooks'
import { useParametricSelector } from 'utils'
import { ClonedComponentByShowModal, UnhappyModal } from 'view/shared/modal'

interface TradingEntityClearingDialogProps extends ClonedComponentByShowModal {
  tradingEntityClearingType: TradingEntityClearingType
  tradingEntityId: string
  workflowId: string
  entityId: string
}

const workflowIdSelector = (_state: {}, ownProps: TradingEntityClearingDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: TradingEntityClearingDialogProps) => ownProps.entityId
const tradingEntityClearingTypeSelector = (_state: {}, ownProps: TradingEntityClearingDialogProps) => ownProps.tradingEntityClearingType

const availableSponsorsSelector = createWorkflowAvailableSponsoredTradingEntityClearingSponsorIdsSelector(
  workflowIdSelector,
  entityIdSelector
)
const availableClearingHousesSelector = createWorkflowAvailableTradingEntityClearingClearingHouseIdsSelector(
  workflowIdSelector,
  tradingEntityClearingTypeSelector
)

const useReferenceDataClearingEntity = createReferenceDataEntityHook(EntityType.TRADING_ENTITY_CLEARING)

export const TradingEntityClearingDialog = (props: TradingEntityClearingDialogProps) => {
  const tradingEntityClearingEntity = useReferenceDataClearingEntity({ workflowId: props.workflowId, entityId: props.entityId })

  if (!tradingEntityClearingEntity) {
    return <UnhappyModal title="Trading Entity Clearing Entity not found." onClose={props.onClose} />
  }

  return <TradingEntityClearingDialogComponent {...props} tradingEntityClearingEntity={tradingEntityClearingEntity} />
}

const TradingEntityClearingDialogComponent: React.FC<TradingEntityClearingDialogProps & {
  tradingEntityClearingEntity: ReferenceDataEntityType<TradingEntityClearing>
}> = props => {
  const { tradingEntityClearingType, workflowId, entityId, tradingEntityClearingEntity, onClose, tradingEntityId } = props

  const dispatch = useDispatch()
  const products = useSelector(productRecordSelector)
  const sponsors = useAllEntities(EntityType.SPONSOR)
  const clearingHouses = useAllEntities(EntityType.CLEARING_HOUSE)
  const productSectors = useSelector(clearableProductSectorRecordSelector)
  const availableSponsors = useParametricSelector(availableSponsorsSelector, props)
  const availableClearingHouses = useParametricSelector(availableClearingHousesSelector, props)

  const title = () => {
    if (tradingEntityClearingEntity.entityState === EntityState.NEW) {
      return tradingEntityClearingType === TradingEntityClearingType.DIRECT
        ? Translation.TC_BUTTON_NEW_DIRECT_TRADING_ENTITY_CLEARING
        : Translation.TC_BUTTON_NEW_SPONSORED_TRADING_ENTITY_CLEARING
    } else {
      return tradingEntityClearingType === TradingEntityClearingType.DIRECT
        ? Translation.TC_BUTTON_EDIT_DIRECT_TRADING_ENTITY_CLEARING
        : Translation.TC_BUTTON_EDIT_SPONSORED_TRADING_ENTITY_CLEARING
    }
  }

  const localTradingEntityClearing = useMemo(
    () =>
      tradingEntityClearingEntity.entityState === EntityState.NEW
        ? {
            ...tradingEntityClearingEntity.data,
            type: tradingEntityClearingEntity.data.type || tradingEntityClearingType,
            status: tradingEntityClearingEntity.data.status || EntityStatus.ENABLED,
            productSectorIds: tradingEntityClearingEntity.data.productSectorIds || [],
            tradingEntityId: tradingEntityClearingEntity.data.tradingEntityId || tradingEntityId
          }
        : tradingEntityClearingEntity.data,
    [tradingEntityClearingEntity, tradingEntityClearingType, tradingEntityId]
  )

  // TODO // Temporary until we have an action available to revert an edited change to a given entity
  const initialTradingEntityClearing = useRef(localTradingEntityClearing)

  const handleUpdate = useCallback(
    (update: Partial<TradingEntityClearing>) => {
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData<TradingEntityClearing>({
          entityId: localTradingEntityClearing.id,
          entityType: EntityTypeEnum.TRADING_ENTITY_CLEARING,
          updatedEntity: {
            ...localTradingEntityClearing,
            ...update
          },
          workflowId
        })
      )
    },
    [localTradingEntityClearing, dispatch, workflowId]
  )

  const productsRowData = useMemo(
    (): OnOffProductsPanelGridRow[] => getProductsRowData(localTradingEntityClearing, productSectors, products),
    [localTradingEntityClearing, products, productSectors]
  )

  const handleProductUpdate = useCallback(
    (newProductSectorIds: string[], status: boolean) => {
      const productStatuses =
        (localTradingEntityClearing.productSectorIds &&
          localTradingEntityClearing.productSectorIds.reduce((record, productSectorId) => {
            record[productSectorId] = true
            return record
          }, {} as Record<string, boolean>)) ||
        {}

      newProductSectorIds.forEach(productSectorId => (productStatuses[productSectorId] = status))

      const productSectorIds = Object.keys(productStatuses).filter(productSectorId => productStatuses[productSectorId])

      handleUpdate({
        productSectorIds
      })
    },
    [localTradingEntityClearing, handleUpdate]
  )

  const handleCancel = useCallback(() => {
    if (tradingEntityClearingEntity.entityState === EntityState.NEW) {
      dispatch(
        WorkflowActions.createEntityDeleteReferenceData([{ entityId, entityType: EntityTypeEnum.TRADING_ENTITY_CLEARING }], workflowId)
      )
    } else {
      // TODO // Temporary // See above
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData({
          entityId: initialTradingEntityClearing.current.id,
          entityType: EntityTypeEnum.TRADING_ENTITY_CLEARING,
          updatedEntity: initialTradingEntityClearing.current,
          workflowId
        })
      )
    }

    if (onClose) {
      onClose()
    }
  }, [tradingEntityClearingEntity.entityState, onClose, dispatch, entityId, workflowId])

  const handleSave = useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  useOnMount(() => {
    handleUpdate(localTradingEntityClearing)
  })

  return (
    <ModalWindow dataTestId={'TradingEntityClearingDialog-ModalWindow-Title'} title={Locale.translate(title())}>
      <Content>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <DialogField>
            <SelectboxField
              dataTestId={'TradingEntityClearingDialog-SelectboxField-CCP'}
              value={localTradingEntityClearing.clearingHouseId}
              label={Locale.translate(Translation.TC_CCP)}
              optionKeys={availableClearingHouses}
              getOption={(clearingHouseId: string) => clearingHouses.find(clearingHouse => clearingHouse.id === clearingHouseId)}
              getOptionText={(clearingHouse: ClearingHouse) => clearingHouse.publishedName}
              onUpdate={(clearingHouseId: string) => {
                handleUpdate({
                  clearingHouseId
                })
              }}
            />
          </DialogField>
        </DialogRow>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <DialogField>
            <TextInputField
              dataTestId={'TradingEntityClearingDialog-TextInputField-Code'}
              value={localTradingEntityClearing.swiftMessageId}
              label={Locale.translate(Translation.TC_CODE)}
              onUpdate={(swiftMessageId: string) => {
                handleUpdate({
                  swiftMessageId
                })
              }}
            />
          </DialogField>
          {tradingEntityClearingType === TradingEntityClearingType.SPONSORED && (
            <DialogField>
              <SelectboxField
                dataTestId={'TradingEntityClearingDialog-SelectboxField-SponsoringBank'}
                value={localTradingEntityClearing.sponsorId}
                label={Locale.translate(Translation.TC_SPONSORING_BANK)}
                optionKeys={availableSponsors}
                getOption={(sponsorId: string) => sponsors.find(sponsor => sponsor.id === sponsorId)}
                getOptionText={SponsorHelper.getName}
                onUpdate={(sponsorId: string) => {
                  handleUpdate({
                    sponsorId
                  })
                }}
                disabled={!localTradingEntityClearing.clearingHouseId}
              />
            </DialogField>
          )}
        </DialogRow>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <GridWrapper height={400}>
            <OnOffProductsPanelGrid rowData={productsRowData} onUpdate={handleProductUpdate} />
          </GridWrapper>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow horizontalLayout={HorizontalLayout.Right}>
          <DialogField flex={0}>
            <TextButton dataTestId={'TradingEntityClearingDialog-TextButton-ButtonCancel'} type={TextButtonType.Bad} onClick={handleCancel}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'TradingEntityClearingDialog-TextButton-ButtonOk'}
              type={TextButtonType.Good}
              onClick={handleSave}
              disabled={!!tradingEntityClearingEntity.validationIssues && tradingEntityClearingEntity.validationIssues.length > 0}>
              {Locale.translate(Translation.TC_BUTTON_OK)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
